import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Check, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Force-password-change gate. Renders on top of the entire app whenever the
 * currently-authenticated user has `must_change_password === true`.
 * Cannot be dismissed without successfully rotating the password.
 */
export default function ForcePasswordChangeModal() {
  const { user, changePassword, logout } = useAuth();
  const [cur, setCur] = useState("");
  const [nu, setNu] = useState("");
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || !user.must_change_password) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (nu.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (nu !== conf) { toast.error("New password and confirmation do not match"); return; }
    setBusy(true);
    const res = await changePassword(cur, nu);
    setBusy(false);
    if (res.success) toast.success("Password updated.");
    else toast.error(res.error || "Failed to change password");
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid="force-pwd-modal">
      <form onSubmit={submit} className="bg-zinc-950 border border-amber-700/40 rounded-lg w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-center gap-2 mb-1">
          <Warning size={18} weight="duotone" className="text-amber-400" />
          <h2 className="text-base font-semibold text-zinc-100">Set a new password</h2>
        </div>
        <p className="text-[11px] text-zinc-400 leading-relaxed mb-5">
          Your administrator provisioned this account with a temporary password. Choose a new one to continue.
        </p>
        <div className="space-y-3">
          <Field label="Current temporary password">
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required autoFocus
              data-testid="fpc-current-input" />
          </Field>
          <Field label="New password (min. 8 chars)">
            <Input type="password" value={nu} onChange={(e) => setNu(e.target.value)} required
              data-testid="fpc-new-input" />
          </Field>
          <Field label="Confirm new password">
            <Input type="password" value={conf} onChange={(e) => setConf(e.target.value)} required
              data-testid="fpc-confirm-input" />
          </Field>
        </div>
        <div className="flex items-center justify-between gap-2 mt-6">
          <button type="button" onClick={logout}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 underline underline-offset-2"
            data-testid="fpc-cancel-btn">
            Cancel & sign out
          </button>
          <Button type="submit" size="sm" disabled={busy || !cur || nu.length < 8 || nu !== conf}
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
            data-testid="fpc-submit-btn">
            <Lock size={11} className="mr-1" weight="duotone" /> Set new password
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
